import { DragEvent, useCallback, useState } from 'react';
import DownloadIcon from '@mui/icons-material/Download';
import Resizer from 'react-image-file-resizer';
import { useDropzone } from 'react-dropzone';
import { CoreBox, CoreDialog, CoreTypography } from '@youscience/core';

import DialogFooter from '@components/DialogFooter';

import { sxStyles, StyledContainer, StyledDialogTitle } from './imageUpload.styles';

interface ImageUploadProps {
  isOpen: boolean;
  onImageUpload: (file: globalThis.File) => void;
  onClose: () => void;
}

export const ImageUpload = (props: ImageUploadProps) => {
  const { isOpen, onClose } = props;

  const [dragActive, setDragActive] = useState(false);

  const setFile = (file: globalThis.File) => {
    props.onImageUpload(file);
    onClose();
  };

  const onDragEnter = (e: DragEvent) => {
    e.nativeEvent.preventDefault();
    e.stopPropagation();

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    setDragActive(false);
  };

  const resizeFile = (file: globalThis.File) =>
    new Promise((resolve) => {
      Resizer.imageFileResizer(
        file,
        5000,
        5000,
        file.type.split('/')[1],
        100,
        0,
        (uri) => {
          resolve(uri);
        },
        'file',
      );
    });

  const onDrop = useCallback(async (acceptedFiles: globalThis.File[]) => {
    if (acceptedFiles.length === 0) {
      return;
    }

    const newFile = (await resizeFile(acceptedFiles[0])) as globalThis.File;

    setFile(newFile);
  }, []);

  const { getRootProps, getInputProps, isDragAccept, isDragReject, isFocused, isFileDialogActive } = useDropzone({
    onDrop,
    onDragEnter,
    onDragLeave,
    accept: {
      'image/png': ['.png'],
      'image/jpeg': ['.jpeg', '.jpg'],
    },
    maxFiles: 1,
  });

  const getColor = (props: {
    isDragAccept: boolean;
    isDragReject: boolean;
    isFocused: boolean;
    isFileDialogActive: boolean;
  }) => {
    if (props.isDragAccept || props.isFileDialogActive) {
      return '#00e676';
    }

    if (props.isDragReject) {
      return '#ff1744';
    }

    return '#2196f3';
  };

  return (
    <CoreDialog sx={sxStyles.dialogWrap} open={isOpen} aria-labelledby="image-upload-title" onClose={onClose}>
      <StyledDialogTitle id="image-upload-title">Select an Image</StyledDialogTitle>

      <CoreBox data-testid="surveyContent" sx={{ padding: '1.5rem', display: 'grid', justifyItems: 'center' }}>
        <StyledContainer
          {...getRootProps({ className: `dropzone${dragActive ? ' drag-active' : ''}` })}
          sx={{ borderColor: getColor(getRootProps({ isDragAccept, isDragReject, isFocused, isFileDialogActive })) }}
        >
          <input {...getInputProps()} />
          <DownloadIcon style={{ pointerEvents: 'none', width: '30px', height: '30px' }} color="primary" />
          <CoreBox>Click or drag file to this area to upload</CoreBox>
          <CoreTypography color="#A8A9AD" style={{ pointerEvents: 'none' }} variant="body2" align="center">
            Make sure your image is in a common format, such as JPG, JPEG, or PNG.
            <br />
          </CoreTypography>
        </StyledContainer>
      </CoreBox>

      <DialogFooter goBackButtonTitle="Cancel" onGoNext={onClose} />
    </CoreDialog>
  );
};
